<template>
  <section>
    <div class="ew-profile-header">
      <navbar-dashboard name="Recargar saldo"></navbar-dashboard>
    </div>

    <div class="ew-view">
      <page-header title="Recargar saldo" subtitle="Agrega fondos a tu monedero para pagar tus envíos más rápido.">
        <router-link to="/prepaids" class="btn btn-outline-secondary">
          <i class="bi bi-arrow-left mr-1"></i> Volver al monedero
        </router-link>
      </page-header>

      <div class="row">
        <!-- COLUMNA IZQUIERDA: monto + método de pago -->
        <div class="col-12 col-md-5">

          <!-- MONTO -->
          <div class="card mb-4">
            <div class="card-header bg-white">
              <h5 class="mb-0">Monto a recargar</h5>
              <small class="text-muted">Elige un monto rápido o ingresa otro</small>
            </div>
            <div class="card-body">
              <div class="d-flex justify-content-end mb-2" v-if="amountLocked">
                <button type="button" class="btn btn-link btn-sm p-0" @click="editAmount">Editar monto</button>
              </div>
              <div class="d-flex flex-wrap mb-3" style="gap: 0.5rem;">
                <button type="button" v-for="preset in amountPresets" :key="preset"
                  class="btn btn-sm" :class="!customAmountSelected && amount === preset ? 'btn-primary' : 'btn-outline-primary'"
                  :disabled="amountLocked" @click="selectPresetAmount(preset)">
                  {{ formatCurrency(preset) }}
                </button>
                <button type="button" class="btn btn-sm"
                  :class="customAmountSelected ? 'btn-primary' : 'btn-outline-primary'"
                  :disabled="amountLocked" @click="selectCustomAmount">
                  Otro
                </button>
              </div>
              <div class="form-group mb-0">
                <input type="text" id="formattedAmount" class="form-control form-control-lg"
                  v-model="customAmountText" @input="onCustomAmountInput" inputmode="decimal"
                  :disabled="amountLocked || !customAmountSelected" placeholder="Ingresa el monto" />
                <small class="form-text text-muted">Ingresa el monto que deseas recargar a tu cuenta.</small>
              </div>
              <hr>
              <div class="d-flex justify-content-between align-items-center">
                <span class="text-muted">Total a recargar</span>
                <span class="font-weight-bold text-primary h5 mb-0">{{ formatCurrency(amount) }}</span>
              </div>
            </div>
          </div>

          <b-alert v-if="prepaidError" variant="danger" show>{{ prepaidError }}</b-alert>

          <!-- MÉTODO DE PAGO -->
          <div class="card">
            <div class="card-header bg-white">
              <h5 class="mb-0">Método de pago</h5>
              <small class="text-muted">Selecciona cómo quieres pagar tu recarga</small>
            </div>
            <div class="card-body">
              <div class="ew-pay-options" role="radiogroup" aria-label="Método de pago">
                <button type="button" class="ew-pay-option" role="radio" :aria-checked="paymentMethod === 'card'"
                  :class="{ 'is-selected': paymentMethod === 'card' }" :disabled="!amount || creatingPrepaid"
                  @click="paymentMethod = 'card'">
                  <i class="bi bi-credit-card"></i>
                  <span class="ew-pay-option-title">Tarjeta</span>
                  <span class="ew-pay-option-sub">Débito o crédito</span>
                </button>
                <button type="button" class="ew-pay-option" role="radio" :aria-checked="paymentMethod === 'paypal'"
                  :class="{ 'is-selected': paymentMethod === 'paypal' }" :disabled="!amount || creatingPrepaid"
                  @click="paymentMethod = 'paypal'">
                  <i class="bi bi-paypal"></i>
                  <span class="ew-pay-option-title">PayPal</span>
                  <span class="ew-pay-option-sub">Cuenta o tarjeta</span>
                </button>
              </div>
              <div v-if="creatingPrepaid" class="text-center mt-2"><b-spinner small variant="primary"></b-spinner></div>
              <small v-if="!amount && !paymentMethod" class="form-text text-muted text-center d-block mt-2 mb-0">
                Ingresa un monto para elegir el método de pago.
              </small>
            </div>
          </div>

        </div>

        <!-- COLUMNA DERECHA: formulario del método de pago elegido -->
        <div class="col-12 col-md-7 mt-4 mt-md-0">
          <div v-if="paymentMethod === 'paypal'">
            <div class="card shadow-sm">
              <div class="card-header bg-white">
                <h5 class="mb-0"><i class="bi bi-paypal mr-1"></i> Paga con PayPal</h5>
              </div>
              <div class="card-body text-center">
                <p class="text-muted mb-3">Ingresa los datos de tu tarjeta de manera segura con PayPal</p>
                <div id="paypal-button-container"></div>
              </div>
              <div class="card-footer text-center text-muted small">
                <i class="bi bi-lock mr-1"></i> Pago seguro con PayPal
              </div>
            </div>
            <div class="text-center mt-4">
              <a href="https://www.paypal.com/webapps/mpp/paypal-popup" target="_blank" title="Cómo funciona PayPal">
                <img src="https://www.paypalobjects.com/webstatic/mktg/logo-center/logotipo_paypal_pagos_seguros.png"
                  alt="Secured by PayPal" class="img-fluid" style="max-height:50px;">
              </a>
            </div>
          </div>

          <div v-else-if="paymentMethod === 'card'">
            <div class="card shadow-sm">
              <div
                class="card-header bg-white d-flex align-items-center justify-content-between">
                <h5 class="mb-0"><i class="bi bi-credit-card mr-1"></i> Paga con tarjeta</h5>
                <div class="d-flex align-items-center">
                  <img :src="visaLogo" alt="Visa" style="height: 18px;" class="mr-2 bg-white rounded p-1">
                  <img :src="mastercardLogo" alt="Mastercard" style="height: 22px;" class="bg-white rounded p-1">
                </div>
              </div>
              <div class="card-body text-left">
                <div class="form-group">
                  <label>Número de tarjeta</label>
                  <input type="text" class="form-control" :value="peiixCardDisplay" @input="onPanInput"
                    inputmode="numeric" autocomplete="cc-number" maxlength="19" placeholder="0000 0000 0000 0000">
                </div>
                <div class="form-row">
                  <div class="form-group col-6">
                    <label>Vencimiento</label>
                    <div class="d-flex">
                      <select class="form-control mr-1" v-model="peiixCard.expMonth">
                        <option value="" disabled>MM</option>
                        <option v-for="m in expMonthOptions" :key="m" :value="m">{{ m }}</option>
                      </select>
                      <select class="form-control" v-model="peiixCard.expYear">
                        <option value="" disabled>AAAA</option>
                        <option v-for="y in expYearOptions" :key="y" :value="y">{{ y }}</option>
                      </select>
                    </div>
                  </div>
                  <div class="form-group col-2">
                    <label>CVV</label>
                    <input type="password" class="form-control" v-model="peiixCard.cvv"
                      @input="peiixCard.cvv = peiixCard.cvv.replace(/\D/g, '').slice(0, 4)" inputmode="numeric"
                      autocomplete="cc-csc" maxlength="4" placeholder="123">
                  </div>
                  <div class="form-group col-4">
                    <label>Titular</label>
                    <input type="text" class="form-control" v-model="peiixCard.holderName" autocomplete="cc-name"
                      placeholder="Como en la tarjeta">
                  </div>
                </div>
                <small v-if="peiixCard.expMonth && peiixCard.expYear && isCardExpired"
                  class="text-danger d-block mb-2">
                  <i class="bi bi-exclamation-circle mr-1"></i>La fecha de vencimiento ya pasó
                </small>
                <b-alert v-if="peiixError" variant="danger" show>{{ peiixError }}</b-alert>
                <button class="btn btn-primary btn-block" @click="openPeiixConfirm"
                  :disabled="peiixLoading || !peiixCardValid">
                  {{ peiixLoading ? 'Procesando...' : 'Recargar con tarjeta' }}
                </button>
              </div>
              <div class="card-footer text-center text-muted small">
                <i class="bi bi-shield-lock-fill mr-1"></i> Tu pago seguro a través de Peiix
              </div>
            </div>
          </div>

          <div v-else class="card ew-recharge-summary">
            <div class="card-header bg-white">
              <h5 class="mb-0">Resumen de tu recarga</h5>
            </div>
            <div class="card-body">
              <ol class="ew-steps">
                <li :class="{ 'is-done': amount }">
                  <span class="ew-step-dot"><i v-if="amount" class="bi bi-check-lg"></i><template v-else>1</template></span>
                  <div>
                    <div class="ew-step-title">Elige el monto</div>
                    <div class="ew-step-text">{{ amount ? formatCurrency(amount) + ' a recargar' : 'Usa un monto rápido o ingresa otro.' }}</div>
                  </div>
                </li>
                <li>
                  <span class="ew-step-dot">2</span>
                  <div>
                    <div class="ew-step-title">Elige el método de pago</div>
                    <div class="ew-step-text">Tarjeta de débito/crédito o PayPal.</div>
                  </div>
                </li>
                <li>
                  <span class="ew-step-dot">3</span>
                  <div>
                    <div class="ew-step-title">Confirma el pago</div>
                    <div class="ew-step-text">El saldo se abona a tu monedero al aprobarse.</div>
                  </div>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>

    <b-modal id="peiix-confirm-modal" v-model="showPeiixConfirmModal" title="Confirmar recarga" hide-footer>
      <p>Estás a punto de recargar <strong>{{ formatCurrency(amount) }}</strong> a tu monedero
        con la tarjeta terminación <strong>{{ peiixCard.pan.slice(-4) }}</strong>.</p>
      <div class="text-right mt-3">
        <button class="btn btn-outline-secondary mr-2" @click="showPeiixConfirmModal = false"
          :disabled="peiixLoading">Cancelar</button>
        <button class="btn btn-primary" @click="confirmPeiixPayment" :disabled="peiixLoading">
          {{ peiixLoading ? 'Procesando...' : 'Confirmar recarga' }}
        </button>
      </div>
    </b-modal>
  </section>
</template>
<script>
import axios from 'axios';
import Navbar from './Header/Navbar';
import commons from '../commons';
import api from "../commons/api";
import visaLogo from '../assets/images/visa.png';
import mastercardLogo from '../assets/images/mastercard.png';

export default {
  name: 'RechargeView',
  components: { 'navbar-dashboard': Navbar },
  data() {
    return {
      amount: 0,
      customAmountText: '',
      customAmountSelected: false,
      paymentMethod: '',
      prepaidId: null,
      paypalLink: '',
      paypalpluslink: '',
      payDisabled: false,
      user: {
        email: '',
        first_names: '',
        last_names: ''
      },
      peiixCard: { pan: '', expMonth: '', expYear: '', cvv: '', holderName: '' },
      peiixLoading: false,
      peiixError: '',
      showPeiixConfirmModal: false,
      visaLogo,
      mastercardLogo,
      amountPresets: [500, 1000, 2000, 5000],
      creatingPrepaid: false,
      prepaidError: '',
    }
  },
  watch: {
    paymentMethod(newVal) {
      if (newVal === 'paypal') {
        this.$nextTick(() => {
          this.createPrepaid();
        });
      } else if (newVal === 'card') {
        this.createPrepaid();
      }
    }
  },
  methods: {
    selectPresetAmount(value) {
      if (this.amountLocked) return;
      this.customAmountSelected = false;
      this.amount = value;
      this.customAmountText = this.formatCurrency(value);
    },
    selectCustomAmount() {
      if (this.amountLocked) return;
      this.customAmountSelected = true;
      this.customAmountText = '';
      this.amount = 0;
      this.$nextTick(() => {
        const el = document.getElementById('formattedAmount');
        if (el) el.focus();
      });
    },
    onCustomAmountInput() {
      // solo dígitos y un punto decimal
      let cleaned = this.customAmountText.replace(/[^0-9.]/g, '');
      const parts = cleaned.split('.');
      if (parts.length > 2) {
        cleaned = parts[0] + '.' + parts.slice(1).join('');
      }
      this.customAmountText = cleaned;
      const parsed = parseFloat(cleaned);
      this.amount = isNaN(parsed) ? 0 : parsed;
    },
    editAmount() {
      this.prepaidError = '';
      this.customAmountSelected = false;
      this.customAmountText = '';
      if (this.prepaidId) {
        this.deletePrepaid();
      } else {
        this.paymentMethod = '';
      }
    },
    formatCurrency(value) {
      return Number(value).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
    },
    deletePrepaid() {
      const id = this.prepaidId;
      this.paymentMethod = '';
      this.prepaidId = null;
      if (!id) return;
      axios
        .delete(`${commons.apiURL}/api/v1/prepaid/${id}`)
        .catch(error => {
          console.error(error);
        });
    },
    createPrepaid() {
      this.prepaidError = '';
      this.creatingPrepaid = true;
      const requestBody = { user: localStorage.getItem('ew-user-id'), amount: this.amount };
      axios.post(`${commons.apiURL}/api/v1/prepaid`, requestBody)
        .then(res => {
          this.prepaidId = res.data.id;
          if (this.paymentMethod === 'paypal') {
            this.initPaypalPayment(this.prepaidId);
          }
        })
        .catch(error => {
          console.error('Error al iniciar la recarga:', error);
          this.prepaidError = 'No se pudo iniciar la recarga. Intenta de nuevo.';
          this.paymentMethod = '';
        })
        .finally(() => {
          this.creatingPrepaid = false;
        });
    },
    onPanInput(event) {
      this.peiixCard.pan = event.target.value.replace(/\D/g, '').slice(0, 19);
    },
    mapPeiixError(raw) {
      if (!raw) return 'No se pudo procesar el pago. Intenta de nuevo.';
      const msg = raw.toUpperCase();
      if (msg.includes('EXPYEAR') || msg.includes('EXPMONTH') || msg.includes('EXPIRATION')) {
        return 'Revisa la fecha de vencimiento de tu tarjeta.';
      }
      if (msg.includes('CVV')) {
        return 'Revisa el código de seguridad (CVV) de tu tarjeta.';
      }
      if (msg.includes('RECHAZ') || msg.includes('DECLIN') || msg.includes('DENIED')) {
        return 'Tu tarjeta fue rechazada. Intenta con otra tarjeta.';
      }
      if (msg.includes('PAN') || msg.includes('CARD NUMBER') || msg.includes('TARJETA')) {
        return 'Revisa el número de tarjeta ingresado.';
      }
      return 'No se pudo procesar el pago. Verifica tus datos e intenta de nuevo.';
    },
    openPeiixConfirm() {
      if (this.paymentMethod !== 'card' || this.peiixLoading || !this.peiixCardValid) return;
      this.peiixError = '';
      this.showPeiixConfirmModal = true;
    },
    async confirmPeiixPayment() {
      if (this.paymentMethod !== 'card' || this.peiixLoading) return;
      this.peiixError = '';
      this.peiixLoading = true;
      try {
        const response = await axios.post(`${commons.apiURL}/api/v1/prepaid/peiix/${this.prepaidId}`, {
          card: this.peiixCard
        });

        if (response.data.message === 'ok') {
          this.showPeiixConfirmModal = false;
          this.$router.push({ path: `/payments/result/success?home=true&email=${this.user.email}` });
        } else {
          this.peiixError = this.mapPeiixError(response.data.error);
          this.peiixCard.pan = '';
          this.peiixCard.cvv = '';
          this.showPeiixConfirmModal = false;
        }
      } catch (error) {
        console.error('Error en recarga con tarjeta:', error);
        this.peiixError = 'Error al procesar el pago. Intenta de nuevo.';
        this.peiixCard.pan = '';
        this.peiixCard.cvv = '';
        this.showPeiixConfirmModal = false;
      } finally {
        this.peiixLoading = false;
      }
    },
    initPaypalPayment(prepaidId) {
      //PÄYPAL
      try {
        const elementExists = !!document.getElementById('paypal-button-container');
        if (elementExists) {
          if (document.getElementById("paypal-button-container").innerHTML != '') {
            document.getElementById("paypal-button-container").innerHTML = "";
          }
        }
        window.paypal
          .Buttons({
            createOrder: async () => {
              try {
                const response = await fetch(
                  `${commons.apiURL}/api/v1/prepaid/v2/link/${prepaidId}`,
                  {
                    method: "GET",
                    headers: {
                      "Content-Type": "application/json",
                    },
                    // use the "body" param to optionally pass additional order information
                    // like product ids and quantities
                  }
                );

                const orderData = await response.json();

                if (orderData.id) {
                  return orderData.id;
                } else {
                  console.log(orderData);
                  const errorDetail = orderData.details[0];
                  const errorMessage = errorDetail
                    ? `${errorDetail.issue} ${errorDetail.description} (${orderData.debug_id})`
                    : JSON.stringify(orderData);

                  throw new Error(errorMessage);
                }
              } catch (error) {
                console.log(`Could not initiate PayPal Checkout...<br><br>${error}`);
              }
            },
            onCancel: () => {
              this.deletePrepaid();
            },
            onError: (err) => {
              this.deletePrepaid();
            },
            onApprove: async (data, actions) => {
              try {
                const response = await fetch(
                  `${commons.apiURL}/api/v1/prepaid/v2/capture-order/${data.orderID}`,
                  {
                    method: "GET",
                  }
                );

                const orderData = await response.json();
                if (orderData.status) {
                  api.prepaid.paymentPrepaidExecute(prepaidId).then((response) => {
                    let data = response.data;
                    if (data.execute) {
                      this.$router.push({ path: `/payments/result/success?home=true&email=${this.user.email}` });
                    } else {
                      this.$router.push({ path: "/payments/result/error" });
                    }
                  }).catch((error) => {
                    this.$router.push({ path: "/payments/result/error" });
                  })
                  return;
                }
                if (!orderData.details) {
                  throw new Error(JSON.stringify(orderData));
                }
                const errorDetail = orderData.details[0];

                if (errorDetail.issue === "INSTRUMENT_DECLINED") {
                  return actions.restart();
                } else if (errorDetail) {
                  // (2) Other non-recoverable errors -> Show a failure message
                  throw new Error(`${errorDetail.description} (${orderData.debug_id})`);
                } else if (!orderData.purchase_units) {
                  throw new Error(JSON.stringify(orderData));
                } else {

                }
              } catch (error) {
                console.log(
                  `Sorry, your transaction could not be processed...<br><br>${error}`
                );
                this.$router.push({ path: "/payments/result/error" });
              }
            },
          })
          .render("#paypal-button-container");
      } catch (error) {
        console.log(
          `Sorry, ${error}`
        );
      }
    },
  },
  computed: {
    amountLocked() {
      return !!this.prepaidId || !!this.paymentMethod;
    },
    peiixCardDisplay() {
      return this.peiixCard.pan.replace(/(.{4})/g, '$1 ').trim();
    },
    expMonthOptions() {
      return Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
    },
    expYearOptions() {
      const current = new Date().getFullYear();
      return Array.from({ length: 11 }, (_, i) => String(current + i));
    },
    isCardExpired() {
      if (!this.peiixCard.expMonth || !this.peiixCard.expYear) return false;
      const now = new Date();
      const expiry = new Date(Number(this.peiixCard.expYear), Number(this.peiixCard.expMonth), 0);
      return expiry < new Date(now.getFullYear(), now.getMonth(), 1);
    },
    peiixCardValid() {
      const pan = this.peiixCard.pan;
      return pan.length >= 13 && pan.length <= 19 &&
        !!this.peiixCard.expMonth && !!this.peiixCard.expYear && !this.isCardExpired &&
        this.peiixCard.cvv.length >= 3 && this.peiixCard.cvv.length <= 4 &&
        !!this.peiixCard.holderName.trim();
    },
  },
  created() {
    if (localStorage.getItem('ew-user-id')) {
      axios.get(`${commons.apiURL}/api/v1/users/${localStorage.getItem('ew-user-id')}`)
        .then(res => {
          this.user.email = res.data.email;
          this.user.first_names = res.data.firstName;
          this.user.last_names = res.data.lastName;
          localStorage.setItem('ew-user-role', res.data.rol);
        });
    }
  }
}
</script>

<style scoped>
.ew-pay-options {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.ew-pay-option {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: 14px;
  background: #fff;
  border: 1.5px solid #dfe2ec;
  border-radius: 10px !important;
  color: #2f3446;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.ew-pay-option i {
  margin-bottom: 4px;
  color: #031d7d;
  font-size: 1.3rem;
}

.ew-pay-option:hover:not(:disabled) {
  border-color: rgba(3, 29, 125, 0.45);
}

.ew-pay-option.is-selected {
  background: rgba(3, 29, 125, 0.04);
  border-color: #031d7d;
  box-shadow: 0 0 0 3px rgba(3, 29, 125, 0.1);
}

.ew-pay-option:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.ew-pay-option-title {
  font-weight: 600;
}

.ew-pay-option-sub {
  color: #6b7185;
  font-size: 0.8rem;
}

.ew-steps {
  margin: 0;
  padding: 0;
  list-style: none;
}

.ew-steps li {
  display: flex;
  gap: 14px;
  padding: 10px 0;
}

.ew-step-dot {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #f1f3f9;
  color: #4a5064;
  font-size: 0.8rem;
  font-weight: 700;
}

.ew-steps li.is-done .ew-step-dot {
  background: #15803d;
  color: #fff;
}

.ew-step-title {
  color: #1c2030;
  font-weight: 600;
}

.ew-step-text {
  color: #6b7185;
  font-size: 0.85rem;
}
</style>



// WEBPACK FOOTER //
// src/components/NewPrepaid.vue